import React, { useEffect, useState, useMemo } from 'react';
import { tns as t, tc } from '../../../i18n/common';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { 
    Button, Layout, Typography, Card, Space, Row, Col, Statistic, Table, Tag, Tooltip, Empty, Spin, Tabs, Result, Grid, Input, Popover, Checkbox, Modal, Divider,
    Drawer, Form, Select, App, message, Popconfirm
} from 'antd';
import { 
    ArrowLeftOutlined, BarChartOutlined, WarningOutlined, CheckCircleOutlined, 
    ExclamationCircleOutlined, DownloadOutlined, TeamOutlined, StopOutlined, QuestionCircleOutlined, ContainerOutlined, SettingOutlined,
    UpOutlined, DownOutlined, EyeInvisibleOutlined, HolderOutlined,
    EditOutlined, EnvironmentOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, UserOutlined
} from '@ant-design/icons';
import { Reorder } from "framer-motion";
import DepartmentEditDrawer from '../Shared/DepartmentEditDrawer';
import TaskCreationModal from '../TaskCreationModal';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

// --- Mock Data / Helper Hook ---
const useOrgHealthData = (tenantId) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // Add error state
    const [data, setData] = useState(null);
    const [stats, setStats] = useState(null);
    const [flatOrgs, setFlatOrgs] = useState([]);
    const [allUsers, setAllUsers] = useState([]); // Store all users for dropdowns
    const [lastUpdated, setLastUpdated] = useState(null);
    const [reloadTrigger, setReloadTrigger] = useState(0);
    const [taskModalVisible, setTaskModalVisible] = useState(false);

    // Listen for data changes from Organigram or other tabs
    useEffect(() => {
        const handleReload = () => setReloadTrigger(prev => prev + 1);
        window.addEventListener('org-data-changed', handleReload);
        return () => window.removeEventListener('org-data-changed', handleReload);
    }, []);

    // Responsive Breakpoints
    const screens = Grid.useBreakpoint();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Correct path: /user (since baseURL is /api)
                const userRes = await axios.post('/user');
                const user = userRes.data;


                const tenantUUID = user.azure_tenant?.azure_tenant_id;
                if (!tenantUUID) throw new Error("No Tenant ID found for user");


                // 3. Fetch Data (Parallel)
                // 3. Fetch Data (Parallel)
                // Try Local Storage First
                let orgTree = null;
                let rawUsers = null;
                let changeLogData = [];

                try {
                     const { loadSecureData } = await import('../../../utils/secureStorage');
                     const localData = await loadSecureData('org_data_cache');
                     const localLog = await loadSecureData('org_changelog_cache');
                     if (localData && Array.isArray(localData)) {
                         // Organigram saves FLAT data (no children). We MUST reconstruct the Tree.
                         // Simple Stratify
                         const idMap = {};
                         localData.forEach(node => { 
                            idMap[node.id] = { ...node, children: [] }; 
                         });
                         
                         const rootNodes = [];
                         localData.forEach(node => {
                             const mappedNode = idMap[node.id];
                             // Use 'parentId' or 'pid' or 'stpid'
                             const pId = node.parentId || node.pid || node.stpid || node.parent_id;
                             if (pId && idMap[pId]) {
                                 idMap[pId].children.push(mappedNode);
                             } else {
                                 rootNodes.push(mappedNode);
                             }
                         });
                         
                         orgTree = rootNodes; 
                         console.log("Analytics: Loaded and Stratified from SecureStorage");
                     }
                     if (localLog && Array.isArray(localLog)) {
                         changeLogData = localLog;
                     }
                } catch (e) {
                    console.warn("Storage load failed", e);
                }

                if (!orgTree || orgTree.length === 0) {
                    const [dataResponse, usersResponse] = await Promise.all([
                        axios.get(`/organigram/${tenantUUID}/data.json`),
                        axios.get(`/organigram/${tenantUUID}/users.json`)
                    ]);
                    orgTree = dataResponse.data;
                    rawUsers = usersResponse.data;
                    
                    if (dataResponse.headers['last-modified']) {
                        const date = new Date(dataResponse.headers['last-modified']);
                        setLastUpdated(date.toLocaleString());
                    }
                } else {
                     // If loaded locally, we still need users for the map if they aren't fully embedded?
                     // Usually organigram data has users embedded in lists, but we might need the full map for lookups or inactive users.
                     // The local data is the "ChartData" (flat list usually for d3-org-chart).
                     // But Analytics expects a Tree or Flat List? 
                     // processNode handles both (Array.isArray check).
                     // However, getting `rawUsers` is still good for the 'Active Users' table if they are separate.
                     // IMPORTANT: The local cache is just the chart nodes. AllUsers might be missing if we don't fetch users.json.
                     const usersResponse = await axios.get(`/organigram/${tenantUUID}/users.json`);
                     rawUsers = usersResponse.data;
                }

                let usersMap = {};
                let userList = [];

                // Normalization Helper
                const normalizeUser = (u) => {
                    return {
                        ...u,
                        firstName: u.firstName || u.givenName || '',
                        lastName: u.lastName || u.surname || '',
                        email: u.email || u.mail || '',
                        externalId: u.externalUserId || u.externalId || '',
                        isActive: (u.enabled !== false) && (!u.disabled) && (!u.isDisabled),
                        isActivated: (u.enabled !== false) // New flag for activation status
                    };
                };

                if (Array.isArray(rawUsers)) {
                    userList = rawUsers;
                    // Create map for easy lookup
                    usersMap = userList.reduce((acc, u) => {
                        acc[u.id] = normalizeUser(u); 
                        return acc;
                    }, {});
                } else {
                    Object.entries(rawUsers).forEach(([k, v]) => {
                        usersMap[k] = normalizeUser(v);
                    });
                }
                
                // Unified Source of Truth for *Normalized* Users
                const allUsers = Object.values(usersMap);
                
                // Metric: Total Headcount = All Non-Banned Users (regardless of activation)
                const totalUsers = allUsers.filter(u => !u.disabled && !u.isDisabled).length;
                const disabledUsers = allUsers.filter(u => u.disabled || u.isDisabled).length;

                // --- Processing Logic for Tree Structure ---
                let totalOrgs = 0;
                let ghostDepts = 0;
                let islands = 0;
                let depthMap = {}; 
                
                // Use Sets for Unique counts to avoid double counting if user is in multiple depts
                const uniqueLeaders = new Set();
                const uniqueMitarbeiter = new Set();
                const uniqueViewers = new Set();
                
                let maxDepth = 0;
                let totalSpan = 0;
                let spanCount = 0;
                
                // User Metrics Map { userId: { leaderCount: 0, employeeCount: 0, managedStaff: 0 } }
                const userMetrics = {};

                const getMetrics = (id) => {
                    if (!userMetrics[id]) userMetrics[id] = { leaderCount: 0, employeeCount: 0, managedStaff: 0 };
                    return userMetrics[id];
                };

                const flattened = [];

                // Helper to get active users
                // Helper to get active users
                const getActiveUsers = (list) => {
                    return (list || []).filter(u => {
                        // Merge local data 'u' OVER server data 'usersMap'
                        // If u has newer 'disabled' status, it should win.
                        const d = { ...(usersMap[u.id] || {}), ...normalizeUser(u) };
                        return !d.disabled && !d.isDisabled;
                    });
                };

                // Helper to pre-calculate subtree stats (Descendant Users)
                const preprocessStats = (node) => {
                    const l = getActiveUsers(node.lists?.leaders || node.leaders);
                    const m = getActiveUsers(node.lists?.mitarbeiter || node.mitarbeiter);
                    const v = getActiveUsers(node.lists?.viewers || node.viewers);
                    const direct = l.length + m.length + v.length;
                    
                    let descendant = 0;
                    if (node.children) {
                        node.children.forEach(child => {
                            descendant += preprocessStats(child);
                        });
                    }
                    node.descendantUsers = descendant;
                    return direct + descendant;
                };

                // Run pre-processing & Role Detection
                if (Array.isArray(orgTree)) {
                    orgTree.forEach(root => preprocessStats(root));
                } else {
                    preprocessStats(orgTree);
                }

                // Determine Roles
                const findUserRoles = (userId) => {
                   const roles = new Set();
                   const traverse = (node) => {
                       if (node.lists) {
                           if (node.lists.leaders && node.lists.leaders.some(u => u.id === userId)) roles.add('leader');
                           if (node.lists.mitarbeiter && node.lists.mitarbeiter.some(u => u.id === userId)) roles.add('employee');
                           if (node.lists.viewers && node.lists.viewers.some(u => u.id === userId)) roles.add('viewer');
                       }
                       if (node.children) node.children.forEach(traverse);
                   };
                   if (Array.isArray(orgTree)) orgTree.forEach(traverse);
                   else traverse(orgTree);
                   
                   if (roles.has('leader')) return 'Leader';
                   if (roles.has('employee')) return 'Employee';
                   if (roles.has('viewer')) return 'Viewer';
                   return '-';
                };

                // Enrich users with Role
                userList = userList.map(u => ({
                    ...normalizeUser(u),
                    role: findUserRoles(u.id)
                }));

                // Recursive Process
                const seenIds = new Set();
                const processNode = (node, level = 0, inheritedParentId = null) => {
                    // 1. Ensure Unique ID
                    let uniqueId = node.id;
                    if (seenIds.has(uniqueId)) {
                        let counter = 1;
                        while (seenIds.has(`${uniqueId}_${counter}`)) {
                            counter++;
                        }
                        uniqueId = `${uniqueId}_${counter}`;
                    }
                    seenIds.add(uniqueId);
                    totalOrgs++;
                    maxDepth = Math.max(maxDepth, level);
                    depthMap[level] = (depthMap[level] || 0) + (node.user_count || 0);

                    const leaders = getActiveUsers(node.lists?.leaders || node.leaders);
                    const mitarbeiter = getActiveUsers(node.lists?.mitarbeiter || node.mitarbeiter);
                    const viewers = getActiveUsers(node.lists?.viewers || node.viewers);

                    // Track Unique Roles & Calculate Metrics
                    leaders.forEach(u => {
                        uniqueLeaders.add(u.id);
                        const m = getMetrics(u.id);
                        m.leaderCount++; 
                        m.managedStaff += mitarbeiter.length; 
                    });
                    
                    mitarbeiter.forEach(u => {
                         uniqueMitarbeiter.add(u.id);
                         getMetrics(u.id).employeeCount++;
                    });
                    
                    viewers.forEach(u => {
                        uniqueViewers.add(u.id);
                    });
                    
                    // Span of Control (Local Metric - remains valid per node)
                    if (leaders.length > 0) {
                        const span = mitarbeiter.length / leaders.length;
                        totalSpan += span;
                        spanCount++;
                    }

                    // Ghost / Health Check
                    let status = 'healthy';
                    let issues = [];

                    const directUsers = leaders.length + mitarbeiter.length + viewers.length;

                    if (directUsers === 0) {
                         if (node.descendantUsers > 0) {
                             status = 'container';
                             
                             // Check for Redundant Container
                             // Condition: Only 1 active child, and that child has no active children (leaf)
                             const activeChildren = node.children ? node.children.filter(c => c.enabled) : [];
                             if (activeChildren.length === 1) {
                                 const child = activeChildren[0];
                                 const grandChildren = child.children ? child.children.filter(gc => gc.enabled) : [];
                                 
                                 // If the single child is a leaf (no grandchildren), then this container is likely redundant
                                 if (grandChildren.length === 0) {
                                     issues.push('redundant');
                                 }
                             }
                         } else {
                             status = 'ghost';
                             issues.push('empty');
                         }
                    } else if (leaders.length === 0 && mitarbeiter.length > 0) {
                        status = 'warning';
                        issues.push('no_leader');
                    } else if (leaders.length > 0 && mitarbeiter.length === 0) {
                        status = 'warning';
                        issues.push('top_heavy');
                    }

                    if (leaders.length > 0 && mitarbeiter.length > 20) {
                         status = 'critical'; 
                         issues.push('overloaded'); 
                    }
                    
                    if (status === 'ghost') ghostDepts++;

                    // Flattened Item
                    flattened.push({
                        key: uniqueId,
                        id: uniqueId,
                        externalId: node.external_id || node.externalId,
                        name: node.name,
                        level,
                        parentId: (inheritedParentId !== null) ? inheritedParentId : ((node.parent_id === "" || node.parent_id === null) ? null : node.parent_id),
                        leaderCount: leaders.length,
                        staffCount: mitarbeiter.length,
                        totalCount: leaders.length + mitarbeiter.length + viewers.length,
                        span: leaders.length > 0 ? (mitarbeiter.length / leaders.length).toFixed(1) : 0,
                        status,
                        issues,
                        leaders: leaders.map(l => ({ ...(usersMap[l.id] || {}), ...l })), 
                        lists: { 
                            leaders: leaders.map(l => ({ ...(usersMap[l.id] || {}), ...l })), 
                            mitarbeiter: mitarbeiter.map(u => ({ ...(usersMap[u.id] || {}), ...u })),
                            viewers: viewers.map(u => ({ ...(usersMap[u.id] || {}), ...u }))
                        },
                        childCount: node.children ? node.children.filter(c => c.enabled).length : 0,
                        stats: {
                            users: leaders.length + mitarbeiter.length + viewers.length,
                            leaders: leaders.length,
                            mitarbeiter: mitarbeiter.length,
                            viewers: viewers.length,
                            descendants: node.descendantUsers || 0,
                            totalSubtree: (node.descendantUsers || 0) + (leaders.length + mitarbeiter.length + viewers.length)
                        }
                    });

                    // Recurse
                    if (node.children) {
                        node.children.forEach(child => processNode(child, level + 1, uniqueId));
                    }
                };

                if (Array.isArray(orgTree)) {
                    orgTree.forEach(root => processNode(root));
                } else {
                    processNode(orgTree);
                }

                // Stats Computation
                const avgSpan = spanCount > 0 ? (totalSpan / spanCount).toFixed(1) : 0;
                
                // New KPI Calculation
                // 1. Flatness Score: (avgSpan / maxDepth) * 10 (Higher is flatter/better usually)
                const flatnessScore = maxDepth > 0 ? ((parseFloat(avgSpan) / maxDepth) * 10).toFixed(1) : 0;

                // 2. Leadership Ratio: (Total Leaders / Total Headcount) * 100
                const leadershipRatio = totalUsers > 0 ? ((uniqueLeaders.size / totalUsers) * 100).toFixed(1) : 0;

                // 3. Department Health Index: (Healthy Depts / Total Depts) * 100
                const healthyDepts = totalOrgs - ghostDepts; // Assuming ghost are the only "unhealthy" for simple index, or better:
                // Let's iterate flattened to count "status === healthy"
                const healthyCount = flattened.filter(f => f.status === 'healthy').length;
                const healthIndex = totalOrgs > 0 ? Math.round((healthyCount / totalOrgs) * 100) : 0;

                // 4. Orphaned Users: Active (not disabled) users in Ghost or Container depts who are NOT activated (enabled=false but valid) or just generally disconnected
                // Per requirement: Active (enabled=false is valid active state? No, enabled=false usually means deactivated. 
                // User said: "active (enabled = false, is also active. only disabled is not active)" 
                // So "Active" = !disabled && !isDisabled. 
                // "Orphaned" = Active users in Ghost/Container nodes who might technically exist but have no real place.
                // Actually Ghost nodes have NO users by definition.
                // So we look for users in "Container" nodes who are direct members of that container?
                // Or maybe users who are in the list but not in any department? (Not possible with tree structure usually)
                // Let's assume Orphaned = Users in "Container" departments (assigned directly to a container).
                const orphanedUsers = flattened
                    .filter(f => f.status === 'container')
                    .reduce((acc, curr) => acc + (curr.leaders?.length || 0) + (curr.staffCount || 0), 0);
                
                // Map metrics back to active users list (and those who are just not activated but not fully disabled)
                // We want to show everyone who is NOT disabled/banned, but distinguish activation
                const enrichedActiveUsers = allUsers
                    .filter(u => (!u.disabled && !u.isDisabled)) // Show all not-banned users
                    .map(u => ({
                        ...u,
                        role: findUserRoles(u.id), // Ensure calculated role is included
                        ...userMetrics[u.id] || { leaderCount: 0, employeeCount: 0, managedStaff: 0 }
                    }));

                setStats({
                    efficiency: 85, // Legacy placeholder, maybe replace with HealthIndex?
                    healthIndex,    // New
                    flatnessScore,  // New
                    leadershipRatio,// New
                    orphanedUsers,  // New
                    ghostDepts,
                    avgSpan,
                    maxDepth,
                    fragmentation: 0, 
                    totalUsers,     // Active users only due to filter above
                    disabledUsers,  // Explicitly disabled
                    roleCounts: {
                        leaders: uniqueLeaders.size,
                        mitarbeiter: uniqueMitarbeiter.size,
                        viewers: uniqueViewers.size
                    },
                    activeUsersList: enrichedActiveUsers
                });
                
                setAllUsers(allUsers);
                setFlatOrgs(flattened);
                setData(orgTree);
                setLoading(false);
                setLoading(false);

            } catch (err) {
                console.error("Failed to load analytics data", err);
                setError(err.message); // Set error
                setLoading(false);
            }
        };

        fetchData();
    }, [tenantId, reloadTrigger]);

    return { loading, error, data, setData, flatOrgs, setFlatOrgs, stats, setStats, allUsers, screens, activeUsers: stats?.activeUsersList || [], taskModalVisible, setTaskModalVisible }; // Quick fix: pass list inside stats or separate state
};

// --- Custom Hook for Preferences ---
const useUserPreferences = (key, defaultColumns) => {
    const [preferences, setPreferences] = useState(defaultColumns);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPreferences = async () => {
            try {
                const res = await axios.get(`/user/preferences/${key}`);
                if (res.data && res.data.value) {
                    setPreferences(res.data.value);
                }
            } catch (err) {
                // Determine if we should warn (404 is expected for new users)
                if (err.response?.status !== 404) {
                    console.warn(`Failed to fetch preferences for ${key}`, err);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchPreferences();
    }, [key]);

    const savePreferences = async (newPreferences) => {
        setPreferences(newPreferences);
        try {
            await axios.post('/user/preferences', { key, value: newPreferences });
        } catch (err) {
            console.error(`Failed to save preferences for ${key}`, err);
        }
    };

    return { preferences, savePreferences, loading };
};

// --- Column Manager Component ---
const ColumnManager = ({ allColumns, visibleKeys, onChange, locale }) => {
    const handleToggle = (key, checked) => {
        if (checked) {
            onChange([...visibleKeys, key]);
        } else {
            onChange(visibleKeys.filter(k => k !== key));
        }
    };

    const move = (index, direction) => {
        const newKeys = [...visibleKeys];
        const [moved] = newKeys.splice(index, 1);
        newKeys.splice(index + direction, 0, moved);
        onChange(newKeys);
    };

    return (
        <div style={{ width: 320, maxHeight: 450, overflowY: 'auto' }}>
            <Typography.Text strong style={{ marginBottom: 8, display: 'block' }}>
                {tc('dashboard.table.options.visible_columns', locale) || "Visible Columns (Order)"}
            </Typography.Text>
            
            {/* 1. List of Visible Columns (Ordered & Draggable) */}
            <div style={{ marginBottom: 16 }}>
                <Reorder.Group axis="y" values={visibleKeys} onReorder={onChange} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {visibleKeys.map((key, index) => {
                        const colDef = allColumns.find(c => c.key === key) || { title: key };
                        return (
                            <Reorder.Item 
                                key={key} 
                                value={key} 
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', marginBottom: 4, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, cursor: 'grab', position: 'relative' }}
                                whileDrag={{ scale: 1.02, boxShadow: "0px 4px 12px rgba(0,0,0,0.1)", cursor: 'grabbing', zIndex: 1000 }}
                            >
                               <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <HolderOutlined style={{ color: '#999', cursor: 'grab' }} />
                                    <span style={{ fontSize: 13 }}>{colDef.title}</span>
                               </div>
                               <Space size={2}>
                                    <Button 
                                        size="small" 
                                        type="text" 
                                        icon={<UpOutlined />} 
                                        disabled={index === 0}
                                        onClick={() => move(index, -1)}
                                    />
                                    <Button 
                                        size="small" 
                                        type="text" 
                                        icon={<DownOutlined />} 
                                        disabled={index === visibleKeys.length - 1}
                                        onClick={() => move(index, 1)}
                                    />
                                    <Button 
                                        size="small" 
                                        type="text" 
                                        danger
                                        icon={<EyeInvisibleOutlined />} 
                                        onClick={() => handleToggle(key, false)}
                                    />
                               </Space>
                            </Reorder.Item>
                        );
                    })}
                </Reorder.Group>
                {visibleKeys.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>{tc('dashboard.table.options.no_visible', locale) || "No columns visible"}</Text>}
            </div>

            <Typography.Text strong style={{ marginBottom: 8, display: 'block', fontSize: 12 }}>
                {tc('dashboard.table.options.hidden_columns', locale) || "Hidden Columns"}
            </Typography.Text>
             {/* 2. List of Hidden Columns */}
             <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                 {allColumns.filter(c => !visibleKeys.includes(c.key)).map(c => (
                     <Tag 
                        key={c.key} 
                        style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                        onClick={() => handleToggle(c.key, true)}
                     >
                         + {c.title}
                     </Tag>
                 ))}
                  {allColumns.filter(c => !visibleKeys.includes(c.key)).length === 0 && (
                     <Text type="secondary" style={{ fontSize: 12 }}>{tc('dashboard.table.options.all_visible', locale) || "All columns visible"}</Text>
                  )}
             </div>
        </div>
    );
};

const AnalyticsDashboardContent = () => {
    const { message, modal } = App.useApp();
    const [targetUserToEdit, setTargetUserToEdit] = useState(null);
    // UI Helpers
    const tooltipStyle = { color: '#000', backgroundColor: '#fff' };

    const navigate = useNavigate();
    
    // Language State Listener (to force re-render on locale change)
    // Language State Listener (to force re-render on locale change)
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');
    const [searchText, setSearchText] = useState('');
    const [userSearchText, setUserSearchText] = useState('');
    const [isLegendOpen, setIsLegendOpen] = useState(false);

    useEffect(() => {
        const handleLocaleChange = () => {
             const newLocale = localStorage.getItem('locale') || 'en';
             setLocale(newLocale);
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);
    
    // We initiate hook. In real app, we might need to pass tenantId.
    const { loading, error, data, setData, flatOrgs, setFlatOrgs, stats, setStats, allUsers, screens, activeUsers, taskModalVisible, setTaskModalVisible } = useOrgHealthData();

     // --- DRAWER & EDIT LOGIC ---
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [currentNode, setCurrentNode] = useState(null);
    const [changeLog, setChangeLog] = useState([]);

    // Initial Load of ChangeLog
    useEffect(() => {
        const loadLog = async () => {
             try {
                 const { loadSecureData } = await import('../../../utils/secureStorage');
                 const log = await loadSecureData('org_changelog_cache');
                 if(log && Array.isArray(log)) setChangeLog(log);
             } catch(e) { console.warn("Log load failed", e); }
        };
        loadLog();
    }, []);

    // Save Logic
    useEffect(() => {
        if (!loading && data && (flatOrgs.length > 0 || changeLog.length > 0)) {
            const saveData = async () => {
                try {
                    const { saveSecureData } = await import('../../../utils/secureStorage');
                     // Organigram expects FLAT list (it stratifies internally or uses d3-org-chart flat input).
                     // We must save FLAT data. 'flatOrgs' contains the flattened structure.
                     // However, we must ensure it has 'parentId' or equivalent for Stratify to work later.
                     
                     // Helper to clean flat data node (remove React/Table specifics if needed)
                     const cleanNode = (n) => {
                         // We need key, id, parentId, name, lists (leaders etc), issues, metrics
                         // Removing UI specific keys that might cause issues?
                         // Actually, keeping them is probably fine, but we must ensure NO children array if we want it flat?
                         // D3 stratify needs children to be null or handled.
                         // But Organigram's load logic: `const { parent, children, ...rest } = node` implies it wants NO children property in the saved file.
                         const { children, parent, ...rest } = n;
                         return rest;
                     };
                     
                     const cleanData = flatOrgs.map(cleanNode);

                     await Promise.all([
                        saveSecureData('org_data_cache', cleanData),
                        saveSecureData('org_changelog_cache', changeLog)
                     ]);
                } catch (e) { console.error("Save failed", e); }
            };
            const timer = setTimeout(saveData, 1000);
            return () => clearTimeout(timer);
        }
    }, [data, changeLog, loading, flatOrgs]);

    const logChange = (type, key, params, dataSnapshot) => {
        const entry = {
            id: Date.now(),
            timestamp: new Date().toLocaleString(),
            type,
            key, // Translation keys
            params,
            desc: key, 
            data: dataSnapshot
        };
        setChangeLog(prev => [entry, ...prev]);
    };

    // Recursive Update Helper
    const updateTreeData = (nodes, targetId, updateFn) => {
        if (Array.isArray(nodes)) {
            return nodes.map(node => {
                if (node.id === targetId) return updateFn(node);
                if (node.children) return { ...node, children: updateTreeData(node.children, targetId, updateFn) };
                return node;
            });
        }
        // Single Root
        if (nodes && nodes.id === targetId) return updateFn(nodes);
        if (nodes && nodes.children) return { ...nodes, children: updateTreeData(nodes.children, targetId, updateFn) };
        return nodes;
    };

    const updateFlatOrgs = (updatedNode) => {
         // Also update flatOrgs for immediate table response
         setFlatOrgs(prev => prev.map(o => o.id === updatedNode.id ? { ...o, ...updatedNode } : o));
    };

    // --- Action Functions ---
    const saveOrg = (values) => {
        const updatedNode = { ...currentNode, ...values };
        setCurrentNode(updatedNode);
        
        // Update Tree
        setData(prev => updateTreeData(prev, updatedNode.id, (n) => ({ ...n, ...values })));
        updateFlatOrgs(updatedNode);
        
        logChange('UPDATE', 'logs.org_update', { name: values.name }, { orgId: updatedNode.id, diff: values });
        message.success(tc('organigram_ui.save_success', locale) || "Details saved");
    };

    const checkUniqueOrgExtId = (val, currentId) => {
        // Check local flattened data
        return !flatOrgs.some(o => o.externalId === val && o.id !== currentId);
    };

    const resetChanges = async () => {
        try {
            const { clearSecureData } = await import('../../../utils/secureStorage');
            await clearSecureData('org_data_cache');
            await clearSecureData('org_changelog_cache');
            setReloadTrigger(prev => prev + 1);
            window.location.reload();
        } catch (e) {
            console.error(e);
            window.location.reload();
        }
    };

    const downloadAll = () => {
        const content = changeLog.map(c => {
             const desc = c.key ? tc(c.key, locale, c.params) : (c.desc || '');
             return `[${c.timestamp}] ${c.type}: ${desc}`;
        }).join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'change_log.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    // --- KPI DEFINITIONS ---
    const allKPIs = useMemo(() => [
        {
            key: 'total_headcount',
            title: tc('dashboard.kpi.total_headcount', locale),
            desc: tc('dashboard.kpi_desc.headcount', locale),
            icon: <TeamOutlined />,
            getValue: (s) => s?.totalUsers,
            color: '#1890ff'
        },
        {
            key: 'disabled_users',
            title: tc('dashboard.kpi.disabled_users', locale),
            desc: tc('dashboard.kpi_desc.disabled', locale),
            icon: <StopOutlined />,
            getValue: (s) => s?.disabledUsers,
            valueStyle: { color: '#8c8c8c' },
            color: '#8c8c8c'
        },
        {
            key: 'leaders',
            title: tc('dashboard.kpi.leaders', locale),
            desc: tc('dashboard.kpi_desc.leaders', locale),
            icon: <TeamOutlined />, // Reusing icon or specific
            getValue: (s) => s?.roleCounts?.leaders,
            color: '#722ed1'
        },
        {
            key: 'employees',
            title: tc('dashboard.kpi.employees', locale),
            desc: tc('dashboard.kpi_desc.employees', locale),
            icon: <TeamOutlined />,
            getValue: (s) => s?.roleCounts?.mitarbeiter,
            color: '#13c2c2'
        },
        {
            key: 'viewers',
            title: tc('dashboard.kpi.viewers', locale),
            desc: tc('dashboard.kpi_desc.viewers', locale),
            icon: <TeamOutlined />,
            getValue: (s) => s?.roleCounts?.viewers,
            color: '#fa8c16'
        },
        {
            key: 'efficiency_score',
            title: tc('dashboard.kpi.efficiency_score', locale),
            desc: tc('dashboard.kpi_desc.efficiency', locale),
            icon: <BarChartOutlined />,
            getValue: (s) => s?.efficiency,
            suffix: '%',
            valueStyle: { color: '#3f8600' },
            color: '#3f8600'
        },
        {
            key: 'ghost_depts',
            title: tc('dashboard.kpi.ghost_depts', locale),
            desc: tc('dashboard.kpi_desc.ghost', locale),
            icon: <WarningOutlined />,
            getValue: (s) => s?.ghostDepts,
            valueStyle: (s) => ({ color: s?.ghostDepts > 0 ? '#cf1322' : '#3f8600' }),
            renderPrefix: (s) => s?.ghostDepts > 0 ? <WarningOutlined /> : <CheckCircleOutlined />,
            color: '#cf1322'
        },
        {
            key: 'span_of_control',
            title: tc('dashboard.kpi.span_of_control', locale),
            desc: tc('dashboard.kpi_desc.span', locale),
            icon: <TeamOutlined />,
            getValue: (s) => s?.avgSpan,
            color: '#2f54eb'
        },
        // NEW KPIs
        {
            key: 'health_index',
            title: tc('dashboard.kpi.health_index', locale) || "Health Index",
            desc: tc('dashboard.kpi_desc.health_index', locale) || "Percentage of healthy departments.",
            icon: <CheckCircleOutlined />,
            getValue: (s) => s?.healthIndex,
            suffix: '%',
            color: '#52c41a'
        },
        {
            key: 'flatness_score',
            title: tc('dashboard.kpi.flatness_score', locale) || "Flatness Score",
            desc: tc('dashboard.kpi_desc.flatness', locale) || "Ratio of Span of Control to Depth.",
            icon: <ContainerOutlined />,
            getValue: (s) => s?.flatnessScore,
            color: '#eb2f96'
        },
        {
            key: 'leadership_ratio',
            title: tc('dashboard.kpi.leadership_ratio', locale) || "Leadership Ratio",
            desc: tc('dashboard.kpi_desc.leadership', locale) || "Percentage of workforce in leadership.",
            icon: <TeamOutlined />,
            getValue: (s) => s?.leadershipRatio,
            suffix: '%',
            color: '#faad14'
        },
        {
            key: 'orphaned_users',
            title: tc('dashboard.kpi.orphaned_users', locale) || "Orphaned Users",
            desc: tc('dashboard.kpi_desc.orphaned', locale) || "Active users in Container departments.",
            icon: <WarningOutlined />,
            getValue: (s) => s?.orphanedUsers,
            color: '#f5222d',
            valueStyle: { color: '#f5222d' }
        }
    ], [locale, stats]);

    // Default KPI Order (Exclude optional ones by default as requested)
    const runDefaultHidden = ['viewers', 'disabled_users', 'flatness_score', 'employees'];
    const defaultKPIKeys = allKPIs.map(k => k.key).filter(k => !runDefaultHidden.includes(k));
    
    // Preference Hook for KPIs
    const { preferences: visibleKPIKeys, savePreferences: saveKPIPreferences, loading: kpiLoading } = useUserPreferences('dashboard_kpi_prefs', defaultKPIKeys);

    // --- User Management Logic (Callbacks for Drawer) ---
    const getAllUsers = () => allUsers || [];

    // NOTE: Drawer internal state handles 'mode' now. We just provide data manipulation callbacks.
    
    const removeUser = (user) => {
        if (!user || !user.id || !currentNode) return;

        // Find role
        let role = null;
        ['leaders', 'mitarbeiter', 'viewers'].forEach(r => {
            if (currentNode.lists[r]?.some(u => u.id === user.id)) role = r;
        });

        if (!role) return;
        
        const updatedList = currentNode.lists[role].filter(u => u.id !== user.id);
        const updatedNode = { ...currentNode, lists: { ...currentNode.lists, [role]: updatedList } };
        
        // Update Stats (Deep Clone to avoid reference modification)
        const stats = { ...(updatedNode.stats || { users:0, leaders:0, mitarbeiter:0 }) };
        stats[role] = Math.max(0, (stats[role]||0) - 1);
        stats.users = Math.max(0, (stats.users||0) - 1);
        updatedNode.stats = stats;

        // Update Flat Counts
        if (role === 'leaders') updatedNode.leaderCount = Math.max(0, (updatedNode.leaderCount || 0) - 1);
        if (role === 'mitarbeiter') updatedNode.staffCount = Math.max(0, (updatedNode.staffCount || 0) - 1);
        updatedNode.totalCount = Math.max(0, (updatedNode.totalCount || 0) - 1);

        setCurrentNode(updatedNode);
        setData(prev => updateTreeData(prev, updatedNode.id, (n) => ({ ...n, lists: updatedNode.lists, stats }))); // Sync tree
        updateFlatOrgs(updatedNode);

        logChange('USER_REMOVE', 'logs.user_remove', { name: `${user.firstName} ${user.lastName}`, org: currentNode.name }, { userId: user.id, orgId: currentNode.id });
        message.success(tc('organigram_ui.user_removed', locale));
    };

    const saveUser = (updatedUser) => {
        // UpdatedUser comes from Drawer form + existing User data
        let role = null;
         ['leaders', 'mitarbeiter', 'viewers'].forEach(r => {
            if (currentNode.lists[r]?.some(u => u.id === updatedUser.id)) role = r;
        });

        if (role) {
             const updatedList = currentNode.lists[role].map(u => u.id === updatedUser.id ? updatedUser : u);
             const updatedNode = { ...currentNode, lists: { ...currentNode.lists, [role]: updatedList } };
             
             setCurrentNode(updatedNode);
             setData(prev => updateTreeData(prev, updatedNode.id, (n) => ({ ...n, lists: updatedNode.lists })));
             // No count change
             updateFlatOrgs(updatedNode); 
             
             logChange('USER_UPDATE', 'logs.user_update', { name: `${updatedUser.firstName} ${updatedUser.lastName}` }, { userId: updatedUser.id, diff: updatedUser });
             message.success("User updated");
        }
    };

    const confirmAddUser = (values, role) => {
        // Values from drawer form. Logic similar to before.
        let newUser;
        if (values.type === 'existing') {
            const existing = getAllUsers().find(u => u.id === values.existingUserId);
            if (!existing) return;
            newUser = { ...existing }; // Clone
        } else {
             newUser = {
                id: "usr_" + Date.now(),
                firstName: values.firstName,
                lastName: values.lastName,
                externalId: values.externalId,
                jobTitle: values.jobTitle,
                mail: values.mail,
                language: values.language || 'DE',
                disabled: false
            };
        }

        const list = currentNode.lists[role] || [];
        
        // Prevent duplicates in same role
        if (list.some(u => u.id === newUser.id)) {
            message.warning("User already in this role");
            return;
        }

        const updatedList = [...list, newUser];
        
        const updatedNode = { ...currentNode, lists: { ...currentNode.lists, [role]: updatedList } };
        
        // Update Stats
        const stats = updatedNode.stats || { users:0, leaders:0, mitarbeiter:0 };
        stats[role] = (stats[role]||0) + 1;
        stats.users = (stats.users||0) + 1;
        updatedNode.stats = stats;

        // Update Flat Counts
        if (role === 'leaders') updatedNode.leaderCount = (updatedNode.leaderCount || 0) + 1;
        if (role === 'mitarbeiter') updatedNode.staffCount = (updatedNode.staffCount || 0) + 1;
        updatedNode.totalCount = (updatedNode.totalCount || 0) + 1;

        setCurrentNode(updatedNode);
        setData(prev => updateTreeData(prev, updatedNode.id, (n) => ({ ...n, lists: updatedNode.lists, stats })));
        updateFlatOrgs(updatedNode);

        logChange('USER_ADD', 'logs.user_add', { name: `${newUser.firstName} ${newUser.lastName}`, org: currentNode.name }, { userId: newUser.id, orgId: currentNode.id });
        message.success("User added");
    };

    const checkUniqueUserExtId = (val, currentUserId) => {
        return !allUsers.some(u => u.externalId === val && u.id !== currentUserId);
    };

    const checkUniqueEmail = (val, currentUserId) => {
         return !allUsers.some(u => u.email === val && u.id !== currentUserId);
    };


    const renderDrawerContent = () => {
        if (!currentNode && drawerMode === 'EDIT_ORG') return null;

        if (drawerMode === 'EDIT_ORG') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <Form layout="vertical" initialValues={currentNode} onFinish={saveOrg} key={currentNode.id}>
                        <Form.Item name="name" label={tc('organigram_ui.org_name', locale) || "Name"} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item 
                            name="externalId" 
                            label={tc('organigram_ui.external_id', locale) || "External ID"}
                            rules={[
                                { validator: (_, value) => checkUniqueOrgExtId(value, currentNode?.id) ? Promise.resolve() : Promise.reject(new Error("ID Exists")) }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>{tc('organigram_ui.save_details', locale) || "Save Details"}</Button>
                    </Form>
                    
                    <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                            <h4>{tc('organigram_ui.users', locale) || "Users"}</h4>
                        </div>
                        
                        {['leaders', 'mitarbeiter', 'viewers'].map(role => (
                            <div key={role} style={{ marginBottom: 15 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0f2f5', padding: '5px 10px', borderRadius: 4 }}>
                                    <span style={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 'bold' }}>
                                        {role === 'leaders' ? (tc('organigram_ui.role_leaders', locale) || "Leaders") : 
                                         role === 'mitarbeiter' ? (tc('organigram_ui.role_mitarbeiter', locale) || "Staff") : 
                                         (tc('organigram_ui.role_viewers', locale) || "Viewers")}
                                    </span>
                                    <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => { setAddUserRole(role); handleAddUserClick(role); }} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 5 }}>
                                    {currentNode.lists?.[role]?.map(u => (
                                        <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #f0f0f0', padding: 5, borderRadius: 4 }}>
                                            <div>
                                                <div style={{ fontWeight: 'bold', fontSize: 12, color: (u.disabled || u.isDisabled) ? 'red' : 'inherit' }}>{u.firstName} {u.lastName}</div>
                                                <div style={{ fontSize: 10, color: '#888' }}>{u.jobTitle}</div>
                                            </div>
                                            <div>
                                                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEditUser(u)} />
                                                <Popconfirm title="Remove user?" onConfirm={() => removeUser(u)}>
                                                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            </div>
                                        </div>
                                    ))}
                                    {(!currentNode.lists?.[role] || currentNode.lists[role].length === 0) && <div style={{ fontSize: 11, color: '#ccc', padding: 5 }}>No users</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (drawerMode === 'EDIT_USER') {
            return (
                <Form layout="vertical" initialValues={currentUser} onFinish={saveUser} key={currentUser?.id}>
                    <Form.Item name="firstName" label={tc('organigram_ui.first_name', locale)} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="lastName" label={tc('organigram_ui.last_name', locale)} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="jobTitle" label={tc('organigram_ui.job_title', locale)}>
                        <Input />
                    </Form.Item>
                    <Form.Item 
                         name="externalId" 
                         label={tc('organigram_ui.external_id', locale)}
                         rules={[
                              { validator: (_, value) => checkUniqueUserExtId(value, currentUser?.id) ? Promise.resolve() : Promise.reject(new Error("ID Exists")) }
                         ]}
                    >
                         <Input />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button onClick={() => setDrawerMode('EDIT_ORG')}>{tc('organigram_ui.back', locale) || "Back"}</Button>
                        <Button type="primary" htmlType="submit">{tc('organigram_ui.save_user', locale) || "Save"}</Button>
                    </div>
                </Form>
            );
        }

        if (drawerMode === 'ADD_USER') {
             return (
                <Form layout="vertical" onFinish={confirmAddUser} initialValues={{ type: 'new', language: 'DE' }}>
                    <Form.Item name="type" label={tc('organigram_ui.add_type', locale)}>
                        <Select onChange={(val) => setAddUserType(val)}>
                            <Select.Option value="new">{tc('organigram_ui.type_new', locale) || "New User"}</Select.Option>
                            <Select.Option value="existing">{tc('organigram_ui.type_existing', locale) || "Existing User"}</Select.Option>
                        </Select>
                    </Form.Item>

                    {addUserType === 'existing' ? (
                        <Form.Item name="existingUserId" label={tc('organigram_ui.select_user', locale)} rules={[{ required: true, message: 'Please select a user' }]}>
                             <Select 
                                showSearch
                                placeholder="Search user..."
                                optionFilterProp="label"
                                options={getAllUsers().map(u => ({
                                    value: u.id,
                                    label: `${u.firstName || ''} ${u.lastName || ''} (${u.email || ''})`
                                }))}
                            />
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item name="firstName" label={tc('organigram_ui.first_name', locale)} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="lastName" label={tc('organigram_ui.last_name', locale)} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="jobTitle" label={tc('organigram_ui.job_title', locale)}>
                                <Input />
                            </Form.Item>
                             <Form.Item 
                                name="externalId" 
                                label={tc('organigram_ui.external_id', locale)}
                                rules={[
                                     { validator: (_, value) => checkUniqueUserExtId(value, null) ? Promise.resolve() : Promise.reject(new Error("ID Exists")) }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </>
                    )}

                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button onClick={() => setDrawerMode('EDIT_ORG')}>{tc('organigram_ui.back', locale) || "Back"}</Button>
                        <Button type="primary" htmlType="submit">{tc('organigram_ui.add_user', locale) || "Add User"}</Button>
                    </div>
                </Form>
             );
        }

        return null;
    };

    // Compute Visible KPIs
    const visibleKPIs = useMemo(() => {
        return visibleKPIKeys
            .map(key => allKPIs.find(k => k.key === key))
            .filter(Boolean);
    }, [visibleKPIKeys, allKPIs]);

    if (error) {
        return (
             <Layout style={{ minHeight: '100vh', background: '#f4f4f9', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Result
                    status="error"
                    title="Failed to load dashboard"
                    subTitle={error}
                    extra={
                        <Button type="primary" onClick={() => navigate('/organigram')}>
                            {tc('dashboard.nav.back_to_chart', locale)}
                        </Button>
                    }
                />
            </Layout>
        );
    }

    // Export Logic
    const handleExport = () => {
        const wb = XLSX.utils.book_new();
        
        // Sheet 1: Summary
        const summaryData = [
            ["Metric", "Value"],
            ["Total Headcount", stats.totalUsers],
            ["Disabled Users", stats.disabledUsers],
            ["Leaders", stats.roleCounts.leaders],
            ["Employees", stats.roleCounts.mitarbeiter],
            ["Viewers", stats.roleCounts.viewers],
            ["Avg Span of Control", stats.avgSpan],
            ["Max Depth", stats.maxDepth],
            ["Ghost Departments", stats.ghostDepts]
        ];
        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

        // Sheet 2: Department Audit
        const auditData = flatOrgs.map(o => ({
            "Organization": o.name,
            "ID": o.id,
            "Level": o.level,
            "Status": o.status,
            "Issues": o.issues.join(", "),
            "Leader Count": o.leaderCount,
            "Staff Count": o.staffCount,
            "Span of Control": o.span
        }));
        const wsAudit = XLSX.utils.json_to_sheet(auditData);
        XLSX.utils.book_append_sheet(wb, wsAudit, "Audit");

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'Org_Health_Report.xlsx');
    };




    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Space direction="vertical" align="center">
                    <Spin size="large" />
                    <Text>Analyzing Data...</Text>
                </Space>
            </div>
        );
    }
    
    return (
        <Layout style={{ minHeight: '100vh', background: '#f4f4f9' }}>
            <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' }}>
                <Button 
                    icon={<ArrowLeftOutlined />} 
                    onClick={() => navigate('/organigram')}
                >
                    {(!screens || screens.md) && tc('dashboard.nav.back_to_chart', locale)}
                </Button>
                
                <Button icon={<DownloadOutlined />} onClick={handleExport}>
                    {(!screens || screens.md) && tc('dashboard.header.export_report', locale)}
                </Button>
            </Header>
            <Content className="px-6" style={{ maxWidth: 1600, margin: '0 auto', width: '100%' }}>
                {/* Title and Settings Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, padding: '32px 0 24px 0' }}>
                    <Title level={2} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', whiteSpace: 'normal', lineHeight: 1.2 }}>
                        {tc('dashboard.header.title', locale)}
                    </Title>
                    
                    {/* KPI Manager Popover */}
                    <Space size="small">
                        <Button 
                            type="text" 
                            onClick={() => setIsLegendOpen(true)}
                            icon={<QuestionCircleOutlined />}
                            style={{ color: '#888' }}
                        >
                            {tc('dashboard.legend.open_btn', locale) || "Glossary"}
                        </Button>
                        <Popover 
                            trigger="click"
                            placement="bottomRight"
                            content={
                                <ColumnManager 
                                    allColumns={allKPIs.map(k => ({ key: k.key, title: k.title }))} 
                                    visibleKeys={visibleKPIKeys} 
                                    onChange={saveKPIPreferences} 
                                    locale={locale} 
                                />
                            } 
                        >
                             <Button icon={<SettingOutlined />}></Button>
                        </Popover>
                    </Space>
                </div>

                <Modal
                    title={<span style={{ fontSize: 20, fontWeight: 600 }}>{tc('dashboard.legend.title', locale)}</span>}
                    open={isLegendOpen}
                    onCancel={() => setIsLegendOpen(false)}
                    footer={null}
                    width={720}
                    styles={{ body: { maxHeight: '70vh', overflowY: 'auto', padding: '0 0' } }}
                    centered
                >
                    <Divider style={{ marginTop: 0, marginBottom: 24 }} />

                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16, color: '#888', textTransform: 'uppercase', fontSize: 12, letterSpacing: '1px' }}>
                        {tc('dashboard.legend.kpi_help', locale)}
                    </Typography.Title>
                    
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {allKPIs.map(kpi => {
                            // Map KPI keys to legend keys
                            let legendKey = kpi.key;
                            if (kpi.key === 'total_headcount') legendKey = 'headcount';
                            if (kpi.key === 'active_users_struct') legendKey = 'orphaned';
                            if (kpi.key === 'orphaned_users') legendKey = 'orphaned'; // FIXED: Correct mapping for key 'orphaned_users'
                            if (kpi.key === 'avg_span_of_control') legendKey = 'span';
                            if (kpi.key === 'span_of_control') legendKey = 'span';
                            if (kpi.key === 'max_depth') legendKey = 'depth';
                            if (kpi.key === 'disabled_users') legendKey = 'disabled';
                            if (kpi.key === 'ghost_departments') legendKey = 'ghost';
                            if (kpi.key === 'ghost_depts') legendKey = 'ghost';
                            if (kpi.key === 'health_index') legendKey = 'health_index';
                            if (kpi.key === 'flatness_score') legendKey = 'flatness';
                            if (kpi.key === 'leadership_ratio') legendKey = 'leadership';
                            if (kpi.key === 'efficiency_score') legendKey = 'efficiency';
                            
                            const desc = tc(`dashboard.legend.items.${legendKey}`, locale);

                            return (
                                <div key={kpi.key} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}> 
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                        <Text strong style={{ fontSize: 16, marginRight: 8 }}>{kpi.title}</Text>
                                        {kpi.suffix && <Tag style={{ margin: 0, fontSize: 10 }}>{kpi.suffix}</Tag>}
                                    </div>
                                    <Text style={{ color: '#666', fontSize: 14, lineHeight: 1.5, display: 'block' }}>
                                        {desc || kpi.desc}
                                    </Text>
                                </div>
                            );
                        })}
                    </div>

                    <Divider style={{ margin: '32px 0 24px 0' }} />
                    
                    <Typography.Title level={5} style={{ marginBottom: 16, color: '#888', textTransform: 'uppercase', fontSize: 12, letterSpacing: '1px' }}>
                        {tc('dashboard.legend.table_help', locale) || "Status Legend"}
                    </Typography.Title>
                    <Space direction="vertical" style={{ width: '100%', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Tag color="success" style={{ minWidth: 140, width: 140, textAlign: 'center', marginRight: 16, whiteSpace: 'normal', height: 'auto', padding: '4px 0' }}>{tc('dashboard.legend.good', locale)}</Tag> 
                            <Text style={{ color: '#666', flex: 1 }}>{tc('dashboard.legend.items.health_index', locale)}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Tag color="warning" style={{ minWidth: 140, width: 140, textAlign: 'center', marginRight: 16, whiteSpace: 'normal', height: 'auto', padding: '4px 0' }}>{tc('dashboard.legend.warning', locale)}</Tag> 
                            <Text style={{ color: '#666', flex: 1 }}>{tc('dashboard.legend.items.container', locale)}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Tag color="error" style={{ minWidth: 140, width: 140, textAlign: 'center', marginRight: 16, whiteSpace: 'normal', height: 'auto', padding: '4px 0' }}>{tc('dashboard.legend.critical', locale)}</Tag> 
                            <Text style={{ color: '#666', flex: 1 }}>{tc('dashboard.legend.items.ghost', locale)}</Text>
                        </div>
                    </Space>
                </Modal>


                {/* Zone A: KPI Cards (Reorderable via Settings) */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {visibleKPIs.map((kpi) => (
                            <Col key={kpi.key} xs={12} sm={12} md={12} lg={6} xl={6}>
                                <Card variant="borderless" style={{ height: '100%' }}>
                                <Statistic 
                                    title={
                                        <Space>
                                            {kpi.title} 
                                            <Tooltip color="white" styles={{ body: tooltipStyle }} title={kpi.desc}>
                                                <QuestionCircleOutlined style={{ color: '#bfbfbf', cursor: 'help' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    value={kpi.getValue(stats)} 
                                    prefix={kpi.renderPrefix ? kpi.renderPrefix(stats) : kpi.icon}
                                    suffix={kpi.suffix}
                                    valueStyle={typeof kpi.valueStyle === 'function' ? kpi.valueStyle(stats) : kpi.valueStyle}
                                />
                                </Card>
                            </Col>
                    ))}
                </Row>
                
                {/* Zone C: Department Table */}
                <DepartmentHealthTable 
                    data={flatOrgs} 
                    locale={locale} 
                    navigate={navigate} 
                    screens={screens}
                    setDrawerVisible={setDrawerVisible}
                    setCurrentNode={setCurrentNode}
                />

                {/* Department Edit Drawer (Shared Component) */}
                <DepartmentEditDrawer
                    visible={drawerVisible}
                    onClose={() => { setDrawerVisible(false); setTargetUserToEdit(null); }}
                    forceEditUser={targetUserToEdit}
                    node={currentNode}
                    allUsers={getAllUsers()}
                    locale={locale}
                    onSaveOrg={saveOrg}
                    onAddUser={confirmAddUser}
                    onUpdateUser={saveUser}
                    onRemoveUser={removeUser}
                    checkUniqueOrgExtId={checkUniqueOrgExtId}
                    checkUniqueUserExtId={checkUniqueUserExtId}
                    checkUniqueEmail={checkUniqueEmail}
                />

            {/* Change Notification Bar */}
            {changeLog.length > 0 && (
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, width: '100%',
                    background: '#ffffffff', color: 'white', padding: '10px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    zIndex: 900, boxSizing: 'border-box', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ color: '#000000' }}>
                        {tc('organigram_ui.changes_count', { locale, count: changeLog.length }) || `${changeLog.length} changes`}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button onClick={resetChanges}>{tc('organigram_ui.reset', locale) || "Reset"}</Button>
                        <Button onClick={downloadAll}>{tc('organigram_ui.download_update', locale) || "Download"}</Button>
                        <Button type="primary" onClick={() => setTaskModalVisible(true)}>{tc('tasks.request_change', locale) || "Request Change"}</Button>
                    </div>
                </div>
            )}
            
            <TaskCreationModal 
                visible={taskModalVisible} 
                onClose={() => setTaskModalVisible(false)} 
                changeLog={changeLog}
                chartData={flatOrgs}
            />


                {/* Zone D: Active Users Directory */}
                {/* Zone D: Active Users Directory */}
                <ActiveUsersTable 
                    userList={activeUsers} 
                    locale={locale} 
                    setUserSearchText={setUserSearchText} 
                    userSearchText={userSearchText}
                    flatOrgs={flatOrgs}
                    navigate={navigate}
                    startEditUser={(user, node) => {
                        setCurrentNode(node);
                        setTargetUserToEdit(user);
                        setDrawerVisible(true);
                    }}
                />

            </Content>
        </Layout>
    );
};

const AnalyticsDashboard = () => (
    <App>
        <AnalyticsDashboardContent />
    </App>
);

// Subcomponent for Active Users Table
const ActiveUsersTable = ({ userList, locale, setUserSearchText, userSearchText, flatOrgs, navigate, startEditUser }) => {
    
    const findNodeByUser = (user) => {
        if (!flatOrgs) return null;
        for (const node of flatOrgs) {
            if (node.lists) {
                if (node.lists.leaders?.find(u => u.id === user.id)) return node;
                if (node.lists.mitarbeiter?.find(u => u.id === user.id)) return node;
                if (node.lists.viewers?.find(u => u.id === user.id)) return node;
            }
        }
        return null;
    };

    // 1. Define ALL possible columns first
    const allColumns = [
        {
            title: tc('dashboard.table.cols.activated', locale),
            dataIndex: 'isActivated',
            key: 'activated',
            render: (isActivated) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: isActivated ? '#52c41a' : '#fa8c16',
                        border: '1px solid rgba(0,0,0,0.1)'
                    }} />
                </div>
            ),
            align: 'center',
            width: 100,
            sorter: (a, b) => Number(a.isActivated) - Number(b.isActivated)
        },
        { 
            title: tc('organigram_ui.first_name', locale), 
            dataIndex: 'firstName', 
            key: 'firstName',
            sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || '')
        },
        { 
            title: tc('organigram_ui.last_name', locale), 
            dataIndex: 'lastName', 
            key: 'lastName',
            sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || '')
        },
        { 
            title: tc('organigram_ui.job_title', locale), 
            dataIndex: 'jobTitle', 
            key: 'jobTitle' 
        },
        { 
            title: tc('organigram_ui.email', locale), 
            dataIndex: 'email', 
            key: 'email' 
        },
        {
            title: tc('dashboard.table.cols.role', locale),
            dataIndex: 'role',
            key: 'role',
            filters: [
                { text: 'Leader', value: 'Leader' },
                { text: 'Employee', value: 'Employee' },
                { text: 'Viewer', value: 'Viewer' }
            ],
            onFilter: (value, record) => record.role === value,
        },
        {
            title: "Ext. ID",
            dataIndex: 'externalId',
            key: 'externalId',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: tc('dashboard.table.cols.dpt_employee', locale),
            dataIndex: 'employeeCount',
            key: 'employeeCount',
            sorter: (a, b) => (a.employeeCount || 0) - (b.employeeCount || 0),
        },
        {
            title: tc('dashboard.table.cols.dpt_leader', locale),
            dataIndex: 'leaderCount',
            key: 'leaderCount',
            sorter: (a, b) => (a.leaderCount || 0) - (b.leaderCount || 0),
        },
        {
            title: tc('dashboard.table.cols.amount_employee', locale),
            dataIndex: 'managedStaff',
            key: 'managedStaff',
            sorter: (a, b) => (a.managedStaff || 0) - (b.managedStaff || 0),
        },
        {
            title: "",
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (text, record) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <Tooltip title="Locate User">
                         <Button 
                            icon={<EnvironmentOutlined />} 
                            size="small" 
                            onClick={(e) => {
                                e.stopPropagation();
                                const node = findNodeByUser(record);
                                if (node) {
                                    navigate('/organigram', { 
                                        state: { 
                                            focusId: node.id, 
                                            type: 'org',
                                            searchName: node.name // Focusing the org is better context
                                        } 
                                    });
                                }
                            }} 
                        />
                    </Tooltip>
                    <Tooltip title="Edit User">
                         <Button 
                            icon={<EditOutlined />} 
                            size="small" 
                            onClick={(e) => {
                                e.stopPropagation();
                                const node = findNodeByUser(record);
                                if (node) {
                                    startEditUser(record, node);
                                }
                            }} 
                        />
                    </Tooltip>
                </div>
            )
        }
    ];

    const defaultKeys = allColumns.map(c => c.key);
    
    // 2. Use Preference Hook
    const { preferences: visibleKeys, savePreferences, loading } = useUserPreferences('analytics_active_users_cols_v2', defaultKeys);

    // 3. Compute Columns based on order and visibility
    const visibleColumns = useMemo(() => {
        return visibleKeys
            .map(key => allColumns.find(c => c.key === key))
            .filter(Boolean);
    }, [visibleKeys, locale]); // Re-compute on locale to update titles? titles are in allColumns

    return (
        <div style={{ marginTop: 24, marginBottom: 40 }}>
             <Card 
                title={(() => {
                    const val = tc('dashboard.header.active_users_title', locale);
                    return (val && val !== 'dashboard.header.active_users_title') ? val : "Active Users Directory";
                })()} 
                variant="borderless"
                extra={
                    <Space>
                        <Input 
                            placeholder={tc('search_placeholder', locale)} 
                            style={{ width: 250 }} 
                            onChange={e => setUserSearchText(e.target.value)}
                            allowClear
                        />
                        <Popover 
                            content={
                                <ColumnManager 
                                    allColumns={allColumns} 
                                    visibleKeys={visibleKeys} 
                                    onChange={savePreferences} 
                                    locale={locale} 
                                />
                            } 
                            title={null} 
                            trigger="click" 
                            placement="bottomRight"
                        >
                             <Button icon={<SettingOutlined />} loading={loading} />
                        </Popover>
                    </Space>
                }
            >
                 <Table 
                    columns={visibleColumns} 
                    dataSource={userList.filter(u => {
                        const txt = userSearchText.toLowerCase();
                        return (
                            u.firstName?.toLowerCase().includes(txt) ||
                            u.lastName?.toLowerCase().includes(txt) ||
                            u.email?.toLowerCase().includes(txt) ||
                            u.jobTitle?.toLowerCase().includes(txt) ||
                            u.externalId?.toLowerCase().includes(txt)
                        );
                    })} 
                    pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

// Subcomponent for Department Health Table
const DepartmentHealthTable = ({ data, locale, navigate, setDrawerVisible, setCurrentNode }) => {
     // UI Helpers
     const tooltipStyle = { color: '#000', backgroundColor: '#fff' };
     const [searchText, setSearchText] = useState('');

    const allColumns = [
        {
            title: tc('dashboard.table.cols.status', locale),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'green';
                let icon = <CheckCircleOutlined />;
                if (status === 'ghost') { color = 'red'; icon = <StopOutlined />; } 
                if (status === 'container') { color = 'geekblue'; icon = <ContainerOutlined />; }
                if (status === 'warning') { color = 'orange'; icon = <WarningOutlined />; }
                if (status === 'critical') { color = 'volcano'; icon = <ExclamationCircleOutlined />; }
                 
                // Translate Status
                const translatedStatus = tc(`dashboard.table.status.${status}`, locale);
                const tooltipText = tc(`dashboard.table.status_desc.${status}`, locale);

                return (
                    <Tooltip title={tooltipText} color="white" styles={{ body: tooltipStyle }}>
                        <Tag color={color} icon={icon}>{translatedStatus}</Tag>
                    </Tooltip>
                );
            },
             filters: [
                { text: tc('dashboard.table.status_filter.healthy', locale), value: 'healthy' },
                { text: tc('dashboard.table.status_filter.container', locale), value: 'container' },
                { text: tc('dashboard.table.status_filter.ghost', locale), value: 'ghost' },
                { text: tc('dashboard.table.status_filter.warning', locale), value: 'warning' },
                { text: tc('dashboard.table.status_filter.critical', locale), value: 'critical' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: tc('dashboard.table.cols.dept_name', locale),
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Ext. ID", // Generic label or add translation
            dataIndex: 'externalId',
            key: 'externalId',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: tc('dashboard.table.cols.headcount', locale),
            dataIndex: 'totalCount',
            key: 'totalCount',
            sorter: (a, b) => a.totalCount - b.totalCount,
        },
        {
            title: tc('dashboard.table.cols.span', locale),
            dataIndex: 'span',
            key: 'span',
            sorter: (a, b) => parseFloat(a.span) - parseFloat(b.span),
            render: (text) => parseFloat(text) > 20 ? <Text type="danger">{text}</Text> : text
        },
        {
            title: tc('dashboard.table.cols.child_count', locale),
            dataIndex: 'childCount',
            key: 'childCount',
            sorter: (a, b) => a.childCount - b.childCount,
        },
        {
            title: tc('dashboard.table.cols.issues', locale),
            dataIndex: 'issues',
            key: 'issues',
            render: tags => (
                <>
                    {tags.map(tag => {
                        let color = 'geekblue';
                        if (tag === 'overloaded') color = 'volcano';
                        if (tag === 'no_leader') color = 'orange';
                        if (tag === 'redundant') color = 'purple';
                        
                        // Translate Issues
                        const translatedTag = tc(`dashboard.table.issues.${tag}`, locale);
                        const tooltipText = tc(`dashboard.table.issues_desc.${tag}`, locale);
                        
                        return (
                            <Tooltip title={tooltipText} color="white" styles={{ body: tooltipStyle }} key={tag}>
                                <Tag color={color}>
                                    {translatedTag || tag.toUpperCase().replace('_', ' ')}
                                </Tag>
                            </Tooltip>
                        );
                    })}
                </>
            ),
            filters: [
                { text: tc('dashboard.table.issues_filter.empty', locale), value: 'empty' },
                { text: tc('dashboard.table.issues_filter.no_leader', locale), value: 'no_leader' },
                { text: tc('dashboard.table.issues_filter.top_heavy', locale), value: 'top_heavy' },
                { text: tc('dashboard.table.issues_filter.overloaded', locale), value: 'overloaded' },
                { text: tc('dashboard.table.issues_filter.redundant', locale), value: 'redundant' },
            ],
            onFilter: (value, record) => record.issues.includes(value),
        },
        {
            title: "",
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (text, record) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <Tooltip title="View in Chart">
                        <Button 
                            icon={<EnvironmentOutlined />} 
                            size="small" 
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate('/organigram', { 
                                    state: { 
                                        focusId: record.id, 
                                        type: 'org',
                                        searchName: record.name 
                                    } 
                                });
                            }} 
                        />
                    </Tooltip>
                    <Tooltip title="Edit Department">
                        <Button 
                            icon={<EditOutlined />} 
                            size="small" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentNode(record);
                                setDrawerVisible(true);
                            }} 
                        />
                    </Tooltip>
                </div>
            )
        }
    ];

    const defaultKeys = allColumns.map(c => c.key);
    const { preferences: visibleKeys, savePreferences, loading } = useUserPreferences('analytics_dept_health_cols_v2', defaultKeys);

    const visibleColumns = useMemo(() => {
        return visibleKeys
            .map(key => allColumns.find(c => c.key === key))
            .filter(Boolean);
    }, [visibleKeys, locale]);

    return (
        <div style={{ marginTop: 24 }}>
            <Card 
                title="Department Health Audit" 
                variant="borderless"
                extra={
                    <Space>
                        <Input 
                            placeholder={tc('search_placeholder', locale)} 
                            style={{ width: 250 }} 
                            onChange={e => setSearchText(e.target.value)}
                            allowClear
                        />
                         <Popover 
                            content={
                                <ColumnManager 
                                    allColumns={allColumns} 
                                    visibleKeys={visibleKeys} 
                                    onChange={savePreferences} 
                                    locale={locale} 
                                />
                            } 
                            title={null} 
                            trigger="click" 
                            placement="bottomRight"
                        >
                             <Button icon={<SettingOutlined />} loading={loading} />
                        </Popover>
                    </Space>
                }
            >
                 <Table 
                    columns={visibleColumns} 
                    dataSource={data.filter(item => 
                        item.name.toLowerCase().includes(searchText.toLowerCase())
                    )} 
                    pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        onClick: () => {
                            // Navigate to Organigram with focus state
                            navigate('/organigram', { 
                                state: { 
                                    focusId: record.id, 
                                    type: 'org',
                                    searchName: record.name 
                                } 
                            });
                        },
                        style: { cursor: 'pointer' }
                    })}
                />
            </Card>
        </div>
    );
};

export default AnalyticsDashboard;
